import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type {
	SignupState,
	SignupFormData,
	OtpVerificationData,
	PasswordData,
	AdmissionFormData,
	VerifiedUserData,
} from "./types";

const initialState: SignupState = {
	signupForm: {
		cnic: "",
		phoneNumber: "",
	},
	otpVerification: {
		otp: "",
		isVerified: false,
		verificationId: "",
	},
	passwordData: {
		password: "",
		confirmPassword: "",
	},
	admissionForm: {
		basicInfo: {
			profilePicture: undefined,
			fullName: "",
			cnic: "",
			gender: "",
			religion: "",
			feeMode: "",
			fatherName: "",
			fatherCnic: "",
			dateOfBirth: "",
			disability: "",
			otherDisability: "",
			isHafizQuran: false,
			hasIndustrialExperience: false,
			matricTechVocational: "",
			additionalEducationalDocuments: [],
			maritalStatus: "",
			passportNo: "",
			admissionType: "",
			eSignature: "",
			technologyPreferenceObject: undefined,
			quotaCategory: "",
			quotaCategoryObject: undefined,
		},
		guardianInfo: {
			guardianName: "",
			mobileNo: "",
			occupation: "",
			relationship: "",
			landlineNo: "",
			income: "",
		},
		contactInfo: {
			email: "",
			mobileNo: "",
			socialMedia: "",
			landlineNo: "",
		},
		addressInfo: {
			province: "",
			division: "",
			district: "",
			tehsil: "",
			city: "",
			currentAddress: "",
		},
		qualification: {
			matriculation: {
				level: "",
				major: "",
				board: "",
				rollNo: "",
				passingYear: "",
				percentage: 0,
				englishMarks: undefined,
				mathMarks: undefined,
				physicsMarks: undefined,
				chemistryMarks: undefined,
			},
			intermediate: {
				level: "",
				major: "",
				board: "",
				rollNo: "",
				passingYear: "",
				percentage: 0,
			},
			bachelor: {
				level: "",
				degreeName: "",
				instituteName: "",
				registrationNo: "",
				passingYear: "",
				obtainedCGPA: 0,
				totalCGPA: "4.0",
			},
		},
		termsAccepted: false,
		remarks: "",
	},
	currentStep: "signup",
	isComplete: false,
	verifiedUserData: null,
	admissionBasicInfoResetCount: 0,
	admissionQualificationResetCount: 0,
};

const signupSlice = createSlice({
	name: "signup",
	initialState,
	reducers: {
		// Step 1: Update signup form data
		updateSignupForm: (state, action: PayloadAction<Partial<SignupFormData>>) => {
			state.signupForm = { ...state.signupForm, ...action.payload };
		},
		// Step 2: Update OTP verification data
		updateOtpVerification: (state, action: PayloadAction<Partial<OtpVerificationData>>) => {
			state.otpVerification = { ...state.otpVerification, ...action.payload };
		},
		// Step 3: Update password data
		updatePasswordData: (state, action: PayloadAction<Partial<PasswordData>>) => {
			state.passwordData = { ...state.passwordData, ...action.payload };
		},
		// Step 4: Update admission form data
		updateAdmissionForm: (state, action: PayloadAction<Partial<AdmissionFormData>>) => {
			// Handle deep merge for qualification section to preserve all qualification data
			if (action.payload.qualification) {
				state.admissionForm.qualification = {
					...state.admissionForm.qualification,
					...action.payload.qualification,
				};
			}

			// Handle other sections with regular merge
			if (action.payload.basicInfo) {
				state.admissionForm.basicInfo = { ...state.admissionForm.basicInfo, ...action.payload.basicInfo };
			}
			if (action.payload.guardianInfo) {
				state.admissionForm.guardianInfo = { ...state.admissionForm.guardianInfo, ...action.payload.guardianInfo };
			}
			if (action.payload.contactInfo) {
				state.admissionForm.contactInfo = { ...state.admissionForm.contactInfo, ...action.payload.contactInfo };
			}
			if (action.payload.addressInfo) {
				state.admissionForm.addressInfo = { ...state.admissionForm.addressInfo, ...action.payload.addressInfo };
			}
			if (action.payload.termsAccepted !== undefined) {
				state.admissionForm.termsAccepted = action.payload.termsAccepted;
			}
			if (action.payload.remarks !== undefined) {
				state.admissionForm.remarks = action.payload.remarks;
			}
		},
		// Update specific admission form section
		updateAdmissionFormSection: (
			state,
			action: PayloadAction<{
				section: keyof AdmissionFormData;
				data: Record<string, unknown>;
			}>,
		) => {
			const { section, data } = action.payload;

			// Special handling for termsAccepted
			if (section === "termsAccepted") {
				const nextTermsAccepted = data.termsAccepted as boolean;
				if (state.admissionForm.termsAccepted === nextTermsAccepted) {
					return;
				}
				state.admissionForm.termsAccepted = nextTermsAccepted;
			} else if (section === "remarks") {
				const nextRemarks = data.remarks as string;
				if (state.admissionForm.remarks === nextRemarks) {
					return;
				}
				state.admissionForm.remarks = nextRemarks;
			} else {
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				(state.admissionForm[section] as any) = { ...(state.admissionForm[section] as any), ...data };
			}
		},
		// Navigate to next step
		nextStep: state => {
			switch (state.currentStep) {
				case "signup":
					state.currentStep = "otp";
					break;
				case "otp":
					state.currentStep = "password";
					break;
				case "password":
					state.currentStep = "admission";
					break;
				case "admission":
					state.currentStep = "admission";
					break;
			}
		},
		// Navigate to specific step
		goToStep: (state, action: PayloadAction<SignupState["currentStep"]>) => {
			state.currentStep = action.payload;
		},
		// Mark signup as complete
		completeSignup: state => {
			state.isComplete = true;
		},
		// Reset signup data
		resetSignup: state => {
			state.signupForm = initialState.signupForm;
			state.otpVerification = initialState.otpVerification;
			state.passwordData = initialState.passwordData;
			state.admissionForm = initialState.admissionForm;
			state.currentStep = "signup";
			state.isComplete = false;
			state.admissionBasicInfoResetCount = 0;
			state.admissionQualificationResetCount = 0;
		},
		// Reset only Basic Information step (personal, guardian, contact, address)
		resetAdmissionBasicInfo: state => {
			const verified = state.verifiedUserData;
			state.admissionForm.basicInfo = {
				...initialState.admissionForm.basicInfo,
				...(verified
					? {
							fullName: `${verified.firstName} ${verified.lastName}`.trim(),
							cnic: verified.cnic,
						}
					: {}),
			} as typeof initialState.admissionForm.basicInfo;
			state.admissionForm.guardianInfo = { ...initialState.admissionForm.guardianInfo } as typeof initialState.admissionForm.guardianInfo;
			state.admissionForm.contactInfo = {
				...initialState.admissionForm.contactInfo,
				...(verified
					? {
							email: verified.emailAddress,
							mobileNo: verified.phoneNumber,
						}
					: {}),
			} as typeof initialState.admissionForm.contactInfo;
			state.admissionForm.addressInfo = { ...initialState.admissionForm.addressInfo } as typeof initialState.admissionForm.addressInfo;
			state.admissionBasicInfoResetCount += 1;
		},
		// Reset only Qualification step (qualifications, terms, remarks)
		resetAdmissionQualification: state => {
			state.admissionForm.qualification = { ...initialState.admissionForm.qualification };
			state.admissionForm.termsAccepted = false;
			state.admissionForm.remarks = "";
			state.admissionQualificationResetCount += 1;
		},
		// Signal forms to re-sync from Redux after API re-hydration (update profile flow)
		bumpAdmissionBasicInfoFormReset: state => {
			state.admissionBasicInfoResetCount += 1;
		},
		bumpAdmissionQualificationFormReset: state => {
			state.admissionQualificationResetCount += 1;
		},
		// Clear sensitive data (OTP, password)
		clearSensitiveData: state => {
			state.otpVerification.otp = "";
			state.passwordData.password = "";
			state.passwordData.confirmPassword = "";
		},
		// Set verified user data from OTP verification response
		setVerifiedUserData: (state, action: PayloadAction<VerifiedUserData>) => {
			state.verifiedUserData = action.payload;
			// Also prepopulate the admission form with verified user data
			if (state.admissionForm.basicInfo) {
				state.admissionForm.basicInfo.fullName = `${action.payload.firstName} ${action.payload.lastName}`.trim();
				state.admissionForm.basicInfo.cnic = action.payload.cnic;
			}
			if (state.admissionForm.contactInfo) {
				state.admissionForm.contactInfo.email = action.payload.emailAddress;
				state.admissionForm.contactInfo.mobileNo = action.payload.phoneNumber;
			}
		},
		// Clear verified user data
		clearVerifiedUserData: state => {
			state.verifiedUserData = null;
		},
	},
});

export const {
	updateSignupForm,
	updateOtpVerification,
	updatePasswordData,
	updateAdmissionForm,
	updateAdmissionFormSection,
	nextStep,
	goToStep,
	completeSignup,
	resetSignup,
	resetAdmissionBasicInfo,
	resetAdmissionQualification,
	bumpAdmissionBasicInfoFormReset,
	bumpAdmissionQualificationFormReset,
	clearSensitiveData,
	setVerifiedUserData,
	clearVerifiedUserData,
} = signupSlice.actions;

export default signupSlice.reducer;
